import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { SelfModel } from "@core/models/self.model";
import { Utilities } from "@core/utils/utilities";
import { ToasterSeverities } from "@enums/toaster-severities";
import { SELECT_LOGGED_IN_USER } from "@features/auth/store/auth.selector";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { MessagesService } from "@services/messages.service";
import { NotificationService } from "@services/notification.service";
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
} from "rxjs";

import {
  DOWNLOAD_DOCUMENT,
  LOAD_MESSAGES,
  MARK_AS_READ,
  MESSAGE_ACTION_FAILURE,
  SET_MESSAGES,
  SET_MESSAGES_LOADING,
} from "./messages.action";

@Injectable()
export class MessagesEffects {
  loadMessages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_MESSAGES),
      // After a page reload the user is still loading when the page asks for
      // its messages; wait for it rather than read an undefined id.
      concatMap(() =>
        this.store.select(SELECT_LOGGED_IN_USER).pipe(
          filter((user): user is SelfModel => !!user),
          take(1)
        )
      ),
      mergeMap((user): Observable<Action> => {
        this.store.dispatch(SET_MESSAGES_LOADING({ loading: true }));
        return this.messagesService.getMessages$(user.id).pipe(
          switchMap((messages) => {
            this.store.dispatch(SET_MESSAGES_LOADING({ loading: false }));

            return of(SET_MESSAGES({ data: messages }));
          }),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(SET_MESSAGES_LOADING({ loading: false }));
            return of(MESSAGE_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  markAsRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MARK_AS_READ),
      mergeMap((action): Observable<Action> => {
        return this.messagesService.markAsRead$(action.id).pipe(
          switchMap(() => {
            return EMPTY;
          }),
          catchError((error: HttpErrorResponse) => {
            return of(MESSAGE_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  downloadDocument$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(DOWNLOAD_DOCUMENT),
      mergeMap(
        (action: {
          id: string;
          documentName: string;
          isPdf: boolean;
        }): Observable<Action> => {
          return this.messagesService
            .getDocument$(action.id, action.isPdf)
            .pipe(
              switchMap((file: Blob): Observable<Action> => {
                Utilities.downloadAttachment(file, action.documentName);
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return EMPTY;
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return EMPTY;
              })
            );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private messagesService: MessagesService,
    private store: Store,
    private notificationService: NotificationService
  ) {}
}
